import { SOSSIETY_RED, SOSSIETY_RED_HOVER } from '@/styles/brand';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Home, Plus } from 'lucide-react';
import { SearchIcon } from '../icons/SearchIcon';
import { CalendarIconCustom } from '../icons/CalendarIcon';
import { SocialMattIcon } from '../icons/SocialMattIcon';
import { SignInIcon } from '../icons/SignInIcon';
import { ProfileIcon } from '../icons/ProfileIcon';

export const BottomNavigation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  // Don't show BottomNavigation on authentication pages or for unauthenticated users
  const authPages = ['/', '/reset-password', '/reset-password-sent'];
  if (authPages.includes(location.pathname) || !user) {
    return null;
  }

  const leftItems = [
    {
      name: 'Home',
      originalName: 'home',
      href: '/home',
      current: location.pathname === '/home',
    },
    {
      name: t('navigation.planner'),
      originalName: 'calendar',
      href: '/calendar',
      current: location.pathname === '/calendar',
    },
  ];

  const rightItems = [
    {
      name: t('navigation.sossial'),
      originalName: 'sossial',
      href: '/sossial',
      current: location.pathname === '/sossial' || location.pathname.startsWith('/sossial/'),
    },
    {
      name: t('navigation.profile'),
      originalName: 'profile',
      href: '/profile',
      current: location.pathname === '/profile',
      isAvatar: true,
    },
  ];

  const renderIcon = (originalName: string, isAvatar?: boolean) => {
    if (originalName === 'home') return <Home className="w-6 h-6" />;
    if (originalName === 'calendar') return <CalendarIconCustom className="w-6 h-6" />;
    if (originalName === 'sossial') return <SocialMattIcon className="w-6 h-6" />;
    if (isAvatar) return <ProfileIcon className="w-6 h-6" />;
    return <SignInIcon className="w-6 h-6" />;
  };

  return (
    <nav
      className="fixed left-0 right-0 z-50"
      style={{
        background: 'rgba(40,27,27,0.5)',
        backdropFilter: 'blur(8px)',
        bottom: 0,
        paddingBottom: 'env(safe-area-inset-bottom)',
        overflow: 'visible',
      }}
    >
      <div className="max-w-2xl mx-auto px-4 relative">
        <div className="flex items-end h-16">
          {/* Left items */}
          {leftItems.map((item) => (
            <button
              key={item.originalName}
              onClick={() => navigate(item.href)}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-0 transition-colors duration-200 pb-1 ${
                item.current ? 'text-white' : 'text-white/70 hover:text-white'
              }`}
            >
              {renderIcon(item.originalName)}
              <span className="text-xs mt-1">{item.name}</span>
            </button>
          ))}

          {/* FAB — centre slot: takes up horizontal space, button floats above */}
          <div className="flex-1 flex items-center justify-center relative">
            <button
              onClick={() => navigate('/quick-actions')}
              aria-label="Quick Actions"
              className="rounded-full flex items-center justify-center transition-transform duration-150 active:scale-95 shadow-lg"
              style={{
                backgroundColor: SOSSIETY_RED,
                width: 52,
                height: 52,
                transform: 'translateY(-8px)',
              }}
            >
              <Plus className="w-7 h-7 text-white" strokeWidth={2.5} />
            </button>
          </div>

          {/* Right items */}
          {rightItems.map((item) => (
            <button
              key={item.originalName}
              onClick={() => navigate(item.href)}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-0 transition-colors duration-200 pb-1 ${
                item.current ? 'text-white' : 'text-white/70 hover:text-white'
              }`}
            >
              {renderIcon(item.originalName, item.isAvatar)}
              <span className="text-xs mt-1">{item.name}</span>
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};
